import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleCheck,
    faXmark,
    faInbox,
    faScaleBalanced,
    faArrowRightArrowLeft,
    faFilePdf,
    faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useFlashMessage } from '../../components/FlashMessage';
import HMSButton from '../../components/ui/HMSButton';
import HMSBadge from '../../components/ui/HMSBadge';
import templateHubService from '../../services/templateHubService';
import ValuePreview from './ValuePreview';

const formatTimestamp = (value) => {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch (err) {
        return value;
    }
};

/**
 * The Approvals tab — only rendered when the current user is a designated
 * approver with at least one pending version. Each pending version is resolved
 * against blocksById to recover its block label / format and the currently-live
 * value, so we can show a before → after comparison.
 */
const ApprovalsTab = ({ pending, blocksById, onRefresh, onPreview, previewableDocTypes }) => {
    const { showSuccess, showError } = useFlashMessage();
    // { versionId, mode: 'approve'|'reject' } — the card whose action panel is open.
    const [activeAction, setActiveAction] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [working, setWorking] = useState(false);
    const [previewingId, setPreviewingId] = useState(null);

    // Preview a pending version exactly as it would render if approved. The
    // backend infers doc_type / entity from the version's own block.
    const handlePreview = async (version, block) => {
        setPreviewingId(version.id);
        try {
            const label = block.label || version.block_key || 'Change';
            await onPreview(
                () => templateHubService.getPreviewObjectUrl({ version_id: version.id }),
                `${label} v${version.version_number} preview.pdf`,
                `version-${version.id}`,
            );
        } finally {
            setPreviewingId(null);
        }
    };

    const openAction = (versionId, mode) => {
        setActiveAction({ versionId, mode });
        setNoteText('');
    };

    const closeAction = () => {
        setActiveAction(null);
        setNoteText('');
    };

    const handleApprove = async (versionId) => {
        setWorking(true);
        try {
            await templateHubService.approveVersion(versionId, noteText.trim());
            showSuccess('Approved — the new content is now live.');
            closeAction();
            if (onRefresh) await onRefresh();
        } catch (err) {
            showError(err?.response?.data?.error || 'Could not approve this change.');
        } finally {
            setWorking(false);
        }
    };

    const handleReject = async (versionId) => {
        if (!noteText.trim()) {
            showError('A rejection reason is required.');
            return;
        }
        setWorking(true);
        try {
            await templateHubService.rejectVersion(versionId, noteText.trim());
            showSuccess('Change rejected. The author can revise and resubmit.');
            closeAction();
            if (onRefresh) await onRefresh();
        } catch (err) {
            showError(err?.response?.data?.error || 'Could not reject this change.');
        } finally {
            setWorking(false);
        }
    };

    if (!pending || pending.length === 0) {
        return (
            <div className="thq-card">
                <div className="thq-empty">
                    <FontAwesomeIcon icon={faInbox} className="thq-empty-icon" />
                    <div className="thq-empty-title">Nothing awaiting your approval</div>
                    <p>When a legal content change is submitted, it will appear here for review.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="thq-approvals">
            {pending.map((version) => {
                const block = blocksById[version.block] || {};
                const format = block.content_format || 'plain';
                const isOpen = activeAction && activeAction.versionId === version.id;
                const canPreview = Boolean(onPreview) && previewableDocTypes?.has(block.doc_type);
                const previewBusy = previewingId === version.id;

                return (
                    <div key={version.id} className="thq-approval-card">
                        <div className="thq-approval-head">
                            <div>
                                <div className="thq-approval-eyebrow">
                                    <FontAwesomeIcon icon={faScaleBalanced} />
                                    {block.category_display || 'Content'}
                                    {block.hms_entity_name ? (
                                        <>
                                            <span className="thq-dot">·</span>
                                            {block.hms_entity_name}
                                        </>
                                    ) : null}
                                </div>
                                <h3 className="thq-approval-title">
                                    {block.label || version.block_key || `Block #${version.block}`}
                                </h3>
                            </div>
                            <HMSBadge status="pending">
                                Pending · v{version.version_number}
                            </HMSBadge>
                        </div>

                        <div className="thq-approval-meta">
                            Submitted by <strong>{version.created_by_name || 'Unknown'}</strong>
                            {version.submitted_at ? ` · ${formatTimestamp(version.submitted_at)}` : ''}
                        </div>

                        {version.change_note ? (
                            <div className="thq-change-note-readout">
                                <span className="thq-change-note-label">Change note</span>
                                {version.change_note}
                            </div>
                        ) : null}

                        <div className="thq-diff">
                            <div className="thq-diff-col">
                                <div className="thq-diff-label thq-diff-label-before">Current (live)</div>
                                <ValuePreview value={block.active_value} format={format} />
                            </div>
                            <div className="thq-diff-arrow">
                                <FontAwesomeIcon icon={faArrowRightArrowLeft} />
                            </div>
                            <div className="thq-diff-col">
                                <div className="thq-diff-label thq-diff-label-after">Proposed</div>
                                <ValuePreview value={version.value} format={format} />
                            </div>
                        </div>

                        {isOpen ? (
                            <div className="thq-action-panel">
                                <label className="thq-field-label" htmlFor={`thq-action-note-${version.id}`}>
                                    {activeAction.mode === 'reject'
                                        ? <>Rejection reason <span className="thq-required">*</span></>
                                        : 'Approval notes (optional)'}
                                </label>
                                <textarea
                                    id={`thq-action-note-${version.id}`}
                                    className="thq-action-note"
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    rows={3}
                                    placeholder={activeAction.mode === 'reject'
                                        ? 'Explain what needs to change…'
                                        : 'Any notes for the record…'}
                                    disabled={working}
                                    autoFocus
                                />
                                <div className="thq-action-panel-foot">
                                    <HMSButton variant="secondary" size="compact" onClick={closeAction} disabled={working}>
                                        Cancel
                                    </HMSButton>
                                    {activeAction.mode === 'reject' ? (
                                        <HMSButton
                                            variant="danger" size="compact"
                                            onClick={() => handleReject(version.id)} disabled={working}
                                        >
                                            <FontAwesomeIcon icon={faXmark} />
                                            {working ? 'Rejecting…' : 'Confirm rejection'}
                                        </HMSButton>
                                    ) : (
                                        <HMSButton
                                            variant="success" size="compact"
                                            onClick={() => handleApprove(version.id)} disabled={working}
                                        >
                                            <FontAwesomeIcon icon={faCircleCheck} />
                                            {working ? 'Approving…' : 'Confirm approval'}
                                        </HMSButton>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="thq-approval-foot">
                                {canPreview && (
                                    <HMSButton
                                        variant="ghost" size="compact"
                                        className="thq-approval-preview-btn"
                                        onClick={() => handlePreview(version, block)}
                                        disabled={previewBusy}
                                        title="See the document as it would render if approved"
                                    >
                                        <FontAwesomeIcon icon={previewBusy ? faSpinner : faFilePdf} spin={previewBusy} />
                                        {previewBusy ? 'Preparing…' : 'Preview change'}
                                    </HMSButton>
                                )}
                                <HMSButton
                                    variant="dangerGhost" size="compact"
                                    onClick={() => openAction(version.id, 'reject')}
                                >
                                    <FontAwesomeIcon icon={faXmark} /> Reject
                                </HMSButton>
                                <HMSButton
                                    variant="success" size="compact"
                                    onClick={() => openAction(version.id, 'approve')}
                                >
                                    <FontAwesomeIcon icon={faCircleCheck} /> Approve
                                </HMSButton>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ApprovalsTab;
